// src/pages/RegisterReferralPartner.tsx
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { User, Mail, Phone, Calendar, Lock, ArrowRight, ArrowLeft, AlertCircle, Terminal, CheckCircle2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';

const RegisterReferralPartner: React.FC = () => {
  const navigate = useNavigate();

  const [step, setStep] = useState<'register' | 'verify'>('register');
  const [verificationCode, setVerificationCode] = useState('');
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    birthYear: '',
    password: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) newErrors.firstName = 'Nome é obrigatório';
    if (!formData.lastName.trim()) newErrors.lastName = 'Apelido é obrigatório';
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) 
      newErrors.email = 'Email inválido';
    if (!formData.phone.trim()) newErrors.phone = 'Telefone é obrigatório';
    if (!formData.birthYear || parseInt(formData.birthYear) < 1900 || parseInt(formData.birthYear) > new Date().getFullYear()) 
      newErrors.birthYear = 'Ano de nascimento inválido';

    if (!formData.password) newErrors.password = 'A palavra-passe é obrigatória';
    else if (formData.password.length < 8) newErrors.password = 'Mínimo 8 caracteres';
    else if (formData.password.length > 128) newErrors.password = 'Muito longa (máx. 128 caracteres)';

    if (!formData.confirmPassword) newErrors.confirmPassword = 'Confirme a palavra-passe';
    else if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'As palavras-passe não coincidem';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' });
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setServerError(null);

    if (!validateForm()) {
      toast.error('Por favor corrija os erros');
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${import.meta.env.VITE_API_BASE_URL}/referrals/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          email: formData.email.trim().toLowerCase(),
          phone: formData.phone.trim(),
          birthYear: parseInt(formData.birthYear),
          password: formData.password,
        }),
      });

      const data = await res.json();

      if (!res.ok) throw new Error(data.message || 'Erro ao registar');

      toast.success('Registo efetuado! Verifique o seu email.');
      setStep('verify');

    } catch (err: any) {
      setServerError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (verificationCode.length !== 6) {
      toast.error('Insira o código de 6 dígitos');
      return;
    }

    setLoading(true);
    try {
      await fetch(`${import.meta.env.VITE_API_BASE_URL}/users/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email.trim().toLowerCase(),
          token: verificationCode.trim()
        }),
      });

      toast.success('Conta verificada com sucesso!');
      navigate('/login?type=referral&verified=true');

    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Código inválido ou expirado');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#0B0C1E] text-[#e2e8f0] font-sans antialiased selection:bg-[#52C47C] selection:text-[#0B0C1E] flex items-center justify-center p-6 relative overflow-x-hidden">
      
      {/* Structural Grid */}
      <div className="fixed inset-0 pointer-events-none z-0 flex justify-between px-4 sm:px-12 opacity-[0.03]">
        <div className="w-[1px] h-full bg-white" />
        <div className="w-[1px] h-full bg-white" />
        <div className="w-[1px] h-full bg-white" />
        <div className="w-[1px] h-full bg-white" />
      </div>

      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-[#52C47C]/5 blur-[160px] pointer-events-none" />

      <motion.div 
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-lg bg-[#12142B] border border-white/10 p-8 sm:p-10 shadow-2xl relative z-10 my-8"
      >
        <div className="mb-6">
          <Link to="/" className="inline-flex items-center gap-3 group mb-6">
            <img 
              src="https://meupontodevenda.com/cdn/logo-white.svg" 
              alt="MPV — Meu Ponto de Venda" 
              className="h-6 w-auto object-contain transition-transform group-hover:scale-95"
            />
            <span className="font-mono text-[10px] tracking-widest text-[#8087A8] border-l border-white/10 pl-3">
              PROGRAMA DE PARCEIROS
            </span>
          </Link>
        </div>

        <AnimatePresence mode="wait">
          {step === 'register' ? (
            <motion.div key="register">
              <div className="text-left mb-8">
                <span className="font-mono text-[10px] text-[#52C47C] tracking-widest uppercase block mb-2">
                  // RECOMENDAÇÃO & COMISSÕES
                </span>
                <h2 className="text-3xl font-light text-white tracking-tight mb-2">
                  Registo de Parceiro
                </h2>
                <p className="text-slate-400 text-xs font-light">
                  Ganhe comissões recorrentes recomendando empresas para a plataforma MPV.
                </p>
              </div>

              {serverError && (
                <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 text-red-300 font-mono text-xs flex items-start gap-2">
                  <AlertCircle size={16} className="shrink-0 mt-0.5" />
                  <span>{serverError}</span>
                </div>
              )}

              <form onSubmit={handleRegister} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-mono text-xs text-slate-400 uppercase tracking-wider mb-1.5">Nome</label>
                    <input 
                      name="firstName" 
                      value={formData.firstName} 
                      onChange={handleChange}
                      className={`w-full bg-[#0B0C1E] border text-white text-sm px-4 py-2.5 outline-none focus:border-[#52C47C] focus:ring-1 focus:ring-[#52C47C]/30 transition-all ${
                        errors.firstName ? 'border-red-500/60' : 'border-white/10'
                      }`}
                      placeholder="João" 
                      required 
                    />
                    {errors.firstName && <p className="text-red-400 font-mono text-[10px] mt-1">{errors.firstName}</p>}
                  </div>
                  <div>
                    <label className="block font-mono text-xs text-slate-400 uppercase tracking-wider mb-1.5">Apelido</label>
                    <input 
                      name="lastName" 
                      value={formData.lastName} 
                      onChange={handleChange}
                      className={`w-full bg-[#0B0C1E] border text-white text-sm px-4 py-2.5 outline-none focus:border-[#52C47C] focus:ring-1 focus:ring-[#52C47C]/30 transition-all ${
                        errors.lastName ? 'border-red-500/60' : 'border-white/10'
                      }`}
                      placeholder="Silva" 
                      required 
                    />
                    {errors.lastName && <p className="text-red-400 font-mono text-[10px] mt-1">{errors.lastName}</p>}
                  </div>
                </div>

                <div>
                  <label className="block font-mono text-xs text-slate-400 uppercase tracking-wider mb-1.5">E-mail</label>
                  <input 
                    name="email" 
                    type="email" 
                    value={formData.email} 
                    onChange={handleChange}
                    className={`w-full bg-[#0B0C1E] border text-white text-sm px-4 py-2.5 outline-none focus:border-[#52C47C] focus:ring-1 focus:ring-[#52C47C]/30 transition-all ${
                      errors.email ? 'border-red-500/60' : 'border-white/10'
                    }`}
                    placeholder="seuemail@exemplo.com" 
                    required 
                  />
                  {errors.email && <p className="text-red-400 font-mono text-[10px] mt-1">{errors.email}</p>}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-mono text-xs text-slate-400 uppercase tracking-wider mb-1.5">Telefone</label>
                    <input 
                      name="phone" 
                      value={formData.phone} 
                      onChange={handleChange}
                      className={`w-full bg-[#0B0C1E] border text-white text-sm px-4 py-2.5 outline-none focus:border-[#52C47C] focus:ring-1 focus:ring-[#52C47C]/30 transition-all ${
                        errors.phone ? 'border-red-500/60' : 'border-white/10'
                      }`}
                      placeholder="+258 84 123 4567" 
                      required 
                    />
                    {errors.phone && <p className="text-red-400 font-mono text-[10px] mt-1">{errors.phone}</p>}
                  </div>

                  <div>
                    <label className="block font-mono text-xs text-slate-400 uppercase tracking-wider mb-1.5">Ano de Nascimento</label>
                    <input 
                      name="birthYear" 
                      type="number" 
                      value={formData.birthYear} 
                      onChange={handleChange}
                      min="1900" 
                      max={new Date().getFullYear()}
                      className={`w-full bg-[#0B0C1E] border text-white text-sm px-4 py-2.5 outline-none focus:border-[#52C47C] focus:ring-1 focus:ring-[#52C47C]/30 transition-all ${
                        errors.birthYear ? 'border-red-500/60' : 'border-white/10'
                      }`}
                      placeholder="1995" 
                      required 
                    />
                    {errors.birthYear && <p className="text-red-400 font-mono text-[10px] mt-1">{errors.birthYear}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-mono text-xs text-slate-400 uppercase tracking-wider mb-1.5">Palavra-passe</label>
                    <div className="relative">
                      <input
                        name="password"
                        type="password"
                        value={formData.password}
                        onChange={handleChange}
                        className={`w-full bg-[#0B0C1E] border text-white text-sm px-4 py-2.5 outline-none focus:border-[#52C47C] focus:ring-1 focus:ring-[#52C47C]/30 transition-all ${
                          errors.password ? 'border-red-500/60' : 'border-white/10'
                        }`}
                        placeholder="Mín. 8 caracteres"
                        required
                      />
                    </div>
                    {errors.password && <p className="text-red-400 font-mono text-[10px] mt-1">{errors.password}</p>}
                  </div>

                  <div>
                    <label className="block font-mono text-xs text-slate-400 uppercase tracking-wider mb-1.5">Confirmar</label>
                    <input
                      name="confirmPassword"
                      type="password"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className={`w-full bg-[#0B0C1E] border text-white text-sm px-4 py-2.5 outline-none focus:border-[#52C47C] focus:ring-1 focus:ring-[#52C47C]/30 transition-all ${
                        errors.confirmPassword ? 'border-red-500/60' : 'border-white/10'
                      }`}
                      placeholder="Repita a senha"
                      required
                    />
                    {errors.confirmPassword && <p className="text-red-400 font-mono text-[10px] mt-1">{errors.confirmPassword}</p>}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3.5 bg-[#52C47C] hover:bg-white text-[#0B0C1E] font-mono font-bold text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-2 disabled:opacity-50 mt-6 shadow-lg shadow-[#52C47C]/15"
                >
                  {loading ? 'A processar...' : 'Ativar Conta de Parceiro'}
                  {!loading && <ArrowRight size={16} />}
                </button>
              </form>

              <div className="mt-6 pt-4 border-t border-white/10 text-center font-mono text-xs text-slate-400">
                Já tem conta de parceiro?{' '}
                <Link to="/login" className="text-[#52C47C] font-bold hover:underline uppercase">
                  Aceder →
                </Link>
              </div>
            </motion.div>
          ) : (
            /* PASSO DE VERIFICAÇÃO */
            <motion.div key="verify" className="text-center">
              <div className="w-16 h-16 bg-[#52C47C]/10 border border-[#52C47C]/30 flex items-center justify-center mx-auto mb-6 text-[#52C47C]">
                <Mail size={32} />
              </div>
              <span className="font-mono text-[10px] text-[#52C47C] tracking-widest uppercase block mb-2">
                // VALIDAÇÃO DE SEGURANÇA
              </span>
              <h3 className="text-2xl font-light text-white mb-2 tracking-tight">
                Verifique o seu E-mail
              </h3>
              <p className="text-slate-400 text-xs font-light mb-8">
                Introduza o código de 6 dígitos enviado para<br />
                <span className="text-white font-mono font-medium">{formData.email}</span>
              </p>

              <form onSubmit={handleVerify} className="space-y-6">
                <input
                  type="text"
                  maxLength={6}
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  className="w-full text-center text-3xl tracking-[0.8rem] py-4 bg-[#0B0C1E] border border-white/10 text-[#52C47C] font-mono focus:border-[#52C47C] focus:ring-1 focus:ring-[#52C47C]/30 outline-none transition-all"
                  required
                  autoFocus
                />

                <button
                  type="submit"
                  disabled={loading || verificationCode.length !== 6}
                  className="w-full py-3.5 bg-[#52C47C] hover:bg-white text-[#0B0C1E] font-mono font-bold text-xs uppercase tracking-widest transition-all disabled:opacity-50 shadow-lg shadow-[#52C47C]/15"
                >
                  {loading ? "A validar..." : "Confirmar e Ativar"}
                </button>
              </form>

              <button
                onClick={() => setStep('register')}
                className="mt-6 text-xs font-mono text-slate-500 hover:text-white transition-colors inline-flex items-center gap-1.5 uppercase"
              >
                <ArrowLeft size={12} /> Voltar e corrigir dados
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default RegisterReferralPartner;